import { useState, useMemo } from "react";
import { useDebouncedSearch } from "../../../../hooks/useDebouncedSearch";
import { matchesDashboardFilter } from "../../../../utility/signupDashboard";

export const useSignupFilters = (signups) => {
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: "on_squad",
    event: "all",
    squad: "all",
  });
  const [searchValue, debouncedSearchValue, setSearchValue, clearSearchValue] =
    useDebouncedSearch("");

  const filteredSignups = useMemo(() => {
    const rows = signups.filter((signup) => {
      if (!matchesDashboardFilter(signup, filters.status)) {
        return false;
      }

      if (
        filters.event !== "all" &&
        signup.tournamentEvents?.eventName !== filters.event
      ) {
        return false;
      }

      if (
        filters.squad !== "all" &&
        (signup.squads?.id || signup.squadId) !== filters.squad
      ) {
        return false;
      }

      if (debouncedSearchValue) {
        const searchLower = debouncedSearchValue.toLowerCase();
        const matchesEmail = String(signup.email || "")
          .toLowerCase()
          .includes(searchLower);
        const matchesName = (signup.signupBowlers || []).some((bowler) =>
          String(bowler.name || "")
            .toLowerCase()
            .includes(searchLower)
        );
        if (!matchesEmail && !matchesName) {
          return false;
        }
      }

      return true;
    });

    if (filters.status === "waitlist") {
      return rows.sort((a, b) => {
        const squadA = `${a.squads?.squadDate || ""} ${a.squads?.squadTime || ""}`;
        const squadB = `${b.squads?.squadDate || ""} ${b.squads?.squadTime || ""}`;
        if (squadA !== squadB) return squadA.localeCompare(squadB);
        return (
          new Date(a.createdAt || a.created) -
          new Date(b.createdAt || b.created)
        );
      });
    }
    return rows;
  }, [signups, filters, debouncedSearchValue]);

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
  };

  const defaultFilters = {
    status: "on_squad",
    event: "all",
    squad: "all",
  };

  const clearFilters = () => {
    setFilters(defaultFilters);
    clearSearchValue();
  };

  const setQuickFilter = (status) => {
    setFilters((prev) => ({ ...prev, status }));
  };

  const removeFilter = (filterType) => {
    switch (filterType) {
      case "status":
        setFilters((prev) => ({ ...prev, status: "on_squad" }));
        break;
      case "event":
        setFilters((prev) => ({ ...prev, event: "all" }));
        break;
      case "squad":
        setFilters((prev) => ({ ...prev, squad: "all" }));
        break;
      case "search":
        clearSearchValue();
        break;
      default:
        break;
    }
  };

  return {
    showFilters,
    setShowFilters,
    filters,
    setFilters,
    searchTerm: searchValue,
    setSearchTerm: setSearchValue,
    filteredSignups,
    handleFilterChange,
    clearFilters,
    setQuickFilter,
    removeFilter,
  };
};
